import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import useResizeObserver from 'use-resize-observer'

import Layout from '@/components/Layout'
import SEO from '@/components/Seo'
import { useResizer } from '@/contexts/Resizer'
import styled from '@emotion/styled'
import Hero from '@/components/Feature/Candidatos/Hero'
import { graphql, useStaticQuery } from 'gatsby'
import PhotoListas from '@/components/Feature/Candidatos/Blocks/PhotoListas'
import groupBy from '@/util/groupBy'
import ListasStyle from '@/components/Feature/Candidatos/Blocks/ListasStyle'

let animations = {
  enter: {
    opacity: 1,
    transition: {
      type: 'tween',
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      type: 'tween',
      duration: 0.4,
    },
  },
}

const Main = styled(motion.div)``

const IndexPage = () => {
  const { senadoresElectos, diputadosElectos, infoTerritorial } =
    useStaticQuery(graphql`
      query ElectosQuery {
        senadoresElectos: allGoogleSenadoresSheet(
          filter: { elegido: { eq: "Electo" } }
        ) {
          nodes {
            nombre
            lista
            circunscripcion: circunscripci_n
            partidoPolitico: partidoPol_tico__siCorresponde_
            cantidadDeVotos
            dirtyporcentaje: porcentaje
            numerocandidato: n_meroCandidato
            letralista: letraLista
            fotoImage {
              childImageSharp {
                gatsbyImageData(
                  width: 200
                  height: 200
                  quality: 80
                  placeholder: BLURRED
                  formats: [AUTO, WEBP]
                  outputPixelDensities: [0.25, 1, 2]
                )
              }
            }
            twitterImage {
              childImageSharp {
                original {
                  src
                }
                gatsbyImageData(
                  width: 200
                  height: 200
                  quality: 80
                  placeholder: BLURRED
                  formats: [AUTO, WEBP]
                  outputPixelDensities: [0.25, 1, 2]
                )
              }
            }
          }
        }

        diputadosElectos: allGoogleDiputadosSheet(
          filter: { elegido: { eq: "Electo" } }
        ) {
          nodes {
            nombre
            lista
            distrito
            partidoPolitico: partidoPol_tico__siCorresponde_
            cantidadDeVotos
            dirtyporcentaje: porcentaje
            numerocandidato: n_meroCandidato
            letralista: l
            fotoImage {
              childImageSharp {
                gatsbyImageData(
                  width: 200
                  height: 200
                  quality: 80
                  placeholder: BLURRED
                  formats: [AUTO, WEBP]
                  outputPixelDensities: [0.25, 1, 2]
                )
              }
            }
            twitterImage {
              childImageSharp {
                original {
                  src
                }
                gatsbyImageData(
                  width: 200
                  height: 200
                  quality: 80
                  placeholder: BLURRED
                  formats: [AUTO, WEBP]
                  outputPixelDensities: [0.25, 1, 2]
                )
              }
            }
          }
        }
        infoTerritorial: allGoogleInfoSheet(sort: { fields: orden }) {
          nodes {
            circunscripcion: circunscripci_nSenatorial
            comuna
            distrito
            region
            orden
            nombreCompleto
          }
        }
      }
    `)

  const cleanPercentage = oldporcentaje =>
    oldporcentaje ? oldporcentaje.replace('%', '').replace(',', '.') : null

  diputadosElectos.nodes.map((item, key) => {
    diputadosElectos.nodes[key]['porcentaje'] = cleanPercentage(
      item.dirtyporcentaje,
    )
  })

  senadoresElectos.nodes.map((item, key) => {
    senadoresElectos.nodes[key]['porcentaje'] = cleanPercentage(
      item.dirtyporcentaje,
    )
  })

  const byDistrito = groupBy(diputadosElectos.nodes, 'distrito')
  const byCircunscripcion = groupBy(senadoresElectos.nodes, 'circunscripcion')

  const territorios = []
  const circunscripciones = groupBy(infoTerritorial.nodes, 'circunscripcion')

  for (let circunscripcion in circunscripciones) {
    const tempDistritos = []
    const distritos = groupBy(circunscripciones[circunscripcion], 'distrito')

    for (let distrito in distritos) {
      const listas = groupBy(byDistrito[distrito], 'lista')
      const tempListas = []
      for (let lista in listas) {
        tempListas.push({
          lista,
          diputados: listas[lista].sort((a, b) => b.porcentaje - a.porcentaje),
          letralista: listas[lista][0].letralista
            ? listas[lista][0].letralista
            : 'IND',
        })
      }
      tempDistritos.push({
        distrito,
        listas: tempListas.sort((a, b) =>
          a.letralista.localeCompare(b.letralista),
        ),
        // diputados: byDistrito[distrito],
      })
    }

    const orderedSenadores = byCircunscripcion[circunscripcion]
      ? byCircunscripcion[circunscripcion].sort(
          (a, b) => b.porcentaje - a.porcentaje,
        )
      : []

    territorios.push({
      circunscripcion: circunscripciones[circunscripcion][0].circunscripcion,
      orden: circunscripciones[circunscripcion][0].orden,
      region: circunscripciones[circunscripcion][0].nombreCompleto,
      senadores: orderedSenadores,
      distritos: tempDistritos,
    })
  }

  // console.log('territorisos', territorios)

  const heroContent = {
    title: 'Candidatos electos',
    description:
      'El 21 de noviembre las chilenas y chilenos escogieron a los candidatos y candidatas que conformarán a los diputados, senadores, y presidentes de Chile. La Tercera recopiló toda la información disponible, poniéndola a disposición de sus lectores.',
  }

  return (
    <Main initial="exit" animate="enter" exit="exit" variants={animations}>
      <Layout>
        <SEO />
        <Hero title={heroContent.title} description={heroContent.description} />
        <div>
          {territorios.map(territorio => (
            <ListasStyle
              key={territorio.circunscripcion}
              header={
                <PhotoListas
                  header={
                    <div className="">
                      <h3 className="py-4 -mx-3 text-center bold border-b-1 sm:-mx-8">
                        {territorio.region}
                      </h3>
                      {territorio.senadores.length > 0 && (
                        <h4 className="-mx-3 font-sans text-center text-white uppercase bg-rojo sm:-mx-8">
                          <strong>Senadores</strong> electos en{' '}
                          <strong>
                            Circunscripción {territorio.circunscripcion}
                          </strong>
                        </h4>
                      )}
                    </div>
                  }
                  description={territorio.region}
                  list={territorio.senadores}
                />
              }>
              {territorio.distritos.map(distrito => (
                <div>
                  <h4 className="-mx-3 font-sans text-center text-white uppercase sm:-mx-8 bg-azul bold border-t-1 ">
                    <strong>Diputados</strong> electos para el{' '}
                    <strong>Distrito {distrito.distrito}</strong>
                  </h4>
                  <div>
                    {distrito.listas.map(lista => (
                      <div>
                        <PhotoListas
                          header={
                            <div>
                              <h4 className="px-2 -mx-3 font-sans text-left uppercase bg-gray-300 sm:-mx-8 border-t-1">
                                {lista.letralista} -{' '}
                                <strong>{lista.lista}</strong>{' '}
                                <span className="text-xs ">
                                  (
                                  <strong>
                                    {lista.diputados.length}{' '}
                                    {lista.diputados.length > 1
                                      ? 'diputados'
                                      : 'diputado'}
                                  </strong>
                                  )
                                </span>
                              </h4>
                            </div>
                          }
                          list={lista.diputados}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </ListasStyle>
          ))}
        </div>
      </Layout>
    </Main>
  )
}

export default IndexPage
